import type { AccountOption } from "@app/components/account-combobox";
import ExportToExcelButton from "@app/components/export-to-excel-button";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import BudgetToolbar from "@app/lib/budgets/budget-toolbar";
import { useBudgetEditor } from "@app/lib/budgets/use-budget-editor";
import type { XlsxCell, XlsxTable } from "@app/lib/export-xlsx";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useState } from "react";
import { fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { revenuePage_Query } from "@app/queries/revenuePage_Query.graphql";
import RevenueGrid, { acctRowKey, type CellScope } from "./revenue-grid";
import RevenueCellPanel from "./revenue-cell-panel";

export const revenueQuery = graphql`
  query revenuePage_Query($year: Int) {
    revenueByAccount(year: $year) {
      bankAccount {
        id
        name
        number
        nickname
        last4
      }
      yearTotal
      points {
        month
        value
      }
    }
    bankAccounts {
      id
      name
      nickname
      last4
      number
    }
    allStakeholders {
      id
      name
    }
  }
`;

export const CURRENT_YEAR = new Date().getFullYear();

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function RevenuePage({
  queryRef,
  initialBudgetId = null,
}: {
  queryRef: PreloadedQuery<revenuePage_Query>;
  initialBudgetId?: string | null;
}) {
  const preloaded = usePreloadedQuery(revenueQuery, queryRef);
  const [rows, setRows] = useState(preloaded.revenueByAccount);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(false);
  const [cell, setCell] = useState<CellScope | null>(null);

  // "Actuals vs Planned" editor for the revenue side (no perspective/basis lens).
  const editor = useBudgetEditor({ side: "REVENUE", year });

  // Re-sync the local mirror only when the route hands a NEW query (keyed on
  // queryRef, not preloaded) — an imperative refresh() fetches a different query
  // root (it adds `year`), and keying on `preloaded` would re-emit the stale
  // initial snapshot and clobber the refreshed rows (the relay local-mirror
  // clobber). eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setRows(preloaded.revenueByAccount), [queryRef]);

  const accountOptions: AccountOption[] = preloaded.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const stakeholderOptions: StakeholderOption[] = preloaded.allStakeholders.filter(isRealStakeholder);

  const refresh = (nextYear = year) =>
    fetchQuery<revenuePage_Query>(environment, revenueQuery, { year: nextYear })
      .toPromise()
      .then((fresh) => {
        if (fresh) setRows(fresh.revenueByAccount);
      });

  const changeYear = (next: number) => {
    setYear(next);
    editor.selectBudget(null); // budgets are year-scoped — drop the loaded one
    setLoading(true);
    refresh(next).finally(() => setLoading(false));
  };

  // Deep-link from /budgets (?budget=<id>): load it in planned mode, jumping to
  // its year. Runs once on mount.
  useEffect(() => {
    if (!initialBudgetId) return;
    editor.setMode(true);
    editor.selectBudget(initialBudgetId).then((loaded) => {
      if (loaded && loaded.year !== year) {
        setYear(loaded.year);
        setLoading(true);
        refresh(loaded.year).finally(() => setLoading(false));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The grid as actual / budget / delta (per account + the all-accounts row), for
  // the xlsx export. Year columns sum the typed monthly budgets.
  const buildBudgetExport = (): XlsxTable => {
    const months = rows[0]?.points.map((point) => point.month) ?? [];
    const label = (account: (typeof rows)[number]["bankAccount"]) =>
      account.last4 ? `${account.nickname} ••${account.last4}` : account.nickname;
    const dataRow = (
      rowLabel: string,
      actualOf: (month: string) => number,
      budgetOf: (month: string) => number | null,
      yearActual: number
    ): XlsxCell[] => {
      const cells: XlsxCell[] = [rowLabel];
      let yearBudget = 0;
      let hasBudget = false;
      for (const month of months) {
        const actual = actualOf(month);
        const budget = budgetOf(month);
        if (budget !== null) {
          yearBudget += budget;
          hasBudget = true;
        }
        cells.push(actual, budget, budget === null ? null : actual - budget);
      }
      cells.push(yearActual, hasBudget ? yearBudget : null, hasBudget ? yearActual - yearBudget : null);
      return cells;
    };
    const accountRows = rows.map((row) => {
      const rowKey = acctRowKey(row.bankAccount.id);
      return dataRow(
        label(row.bankAccount),
        (month) => row.points.find((point) => point.month === month)?.value ?? 0,
        (month) => editor.budgetDollars(rowKey, month),
        row.yearTotal
      );
    });
    const monthTotal = (month: string) =>
      rows.reduce((sum, row) => sum + (row.points.find((point) => point.month === month)?.value ?? 0), 0);
    const allBudget = (month: string) => {
      let sum = 0;
      let has = false;
      for (const row of rows) {
        const value = editor.budgetDollars(acctRowKey(row.bankAccount.id), month);
        if (value !== null) {
          sum += value;
          has = true;
        }
      }
      return has ? sum : null;
    };
    const grandActual = rows.reduce((sum, row) => sum + row.yearTotal, 0);
    const columns = [
      { label: "Account", type: "text" as const },
      ...months.flatMap((month) => {
        const abbr = MONTH_LABELS[Number(month.slice(5, 7)) - 1] ?? month;
        return [
          { label: `${abbr} Actual`, type: "money" as const },
          { label: `${abbr} Budget`, type: "money" as const },
          { label: `${abbr} Δ`, type: "money" as const },
        ];
      }),
      { label: "Year Actual", type: "money" as const },
      { label: "Year Budget", type: "money" as const },
      { label: "Year Δ", type: "money" as const },
    ];
    return {
      filename: `revenue-budget-${year}`,
      sheetName: `Revenue vs budget ${year}`,
      columns,
      rows: [...accountRows, dataRow("All accounts", monthTotal, allBudget, grandActual)],
    };
  };

  // The grid as shown: one row per account over the 12 months + per-account year
  // total, then the "All accounts" totals row that ties out to canonical revenue.
  const buildExport = (): XlsxTable => {
    const months = rows[0]?.points.map((p) => p.month) ?? [];
    const accountLabel = (a: (typeof rows)[number]["bankAccount"]) =>
      a.last4 ? `${a.nickname} ••${a.last4}` : a.nickname;
    const monthTotal = (month: string) =>
      rows.reduce((sum, r) => sum + (r.points.find((p) => p.month === month)?.value ?? 0), 0);
    const grandTotal = rows.reduce((sum, r) => sum + r.yearTotal, 0);
    return {
      filename: `revenue-${year}`,
      sheetName: `Revenue ${year}`,
      columns: [
        { label: "Account", type: "text" },
        ...months.map((m) => ({ label: MONTH_LABELS[Number(m.slice(5, 7)) - 1] ?? m, type: "money" as const })),
        { label: "Total", type: "money" as const },
      ],
      rows: [
        ...rows.map((r) => [accountLabel(r.bankAccount), ...r.points.map((p) => p.value ?? 0), r.yearTotal]),
        ["All accounts", ...months.map((m) => monthTotal(m)), grandTotal],
      ],
    };
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-xl font-bold">Revenue</h1>
        <div className="ml-auto flex items-center gap-2">
          <BudgetToolbar editor={editor} buildXlsx={buildBudgetExport} />
          {!editor.mode && rows.length > 0 && <ExportToExcelButton build={buildExport} />}
          <div className="flex items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => changeYear(year - 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
            title="Previous year"
          >
            ‹
          </button>
          <span className="px-2 tabular-nums font-medium text-gray-800">{year}</span>
          <button
            type="button"
            disabled={year >= CURRENT_YEAR}
            onClick={() => changeYear(year + 1)}
            className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Next year"
          >
            ›
          </button>
          </div>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Canonical operating revenue — money from the outside world into your accounts,
        the same figure the P&amp;L and KPIs report. Click any amount to review the
        transactions behind it and fix anything mislabeled.
      </p>

      {loading ? (
        <GridSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState year={year} />
      ) : (
        <RevenueGrid
          rows={rows}
          onCell={setCell}
          budgetMode={editor.mode}
          getBudgetText={editor.budgetText}
          onBudgetChange={editor.setBudgetText}
          getBudgetDollars={editor.budgetDollars}
        />
      )}

      {cell && (
        <RevenueCellPanel
          scope={cell}
          year={year}
          accountOptions={accountOptions}
          stakeholderOptions={stakeholderOptions}
          onClose={() => setCell(null)}
          onChanged={() => refresh()}
        />
      )}
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-3 animate-pulse">
      {Array.from({ length: 6 }).map((_, row) => (
        <div key={row} className="flex items-center gap-3 py-2">
          <div className="h-3 bg-gray-100 rounded w-56" />
          <div className="ml-auto flex gap-3">
            {Array.from({ length: 12 }).map((_, col) => (
              <div key={col} className="h-3 w-10 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ year }: { year: number }) {
  return (
    <div className="border border-dashed border-gray-200 rounded-lg bg-white p-10 text-center">
      <p className="text-sm text-gray-500">No revenue recorded in {year}.</p>
    </div>
  );
}
